/**
 * SVG overlay that implements all drawing tools on top of the Lightweight Charts canvas.
 *
 * Coordinate contract:
 *   - chart.timeScale().coordinateToTime(x)  — pixel x → UTC timestamp
 *   - chart.timeScale().timeToCoordinate(t)  — UTC timestamp → pixel x
 *   - series.coordinateToPrice(y)            — pixel y → price
 *   - series.priceToCoordinate(p)            — price → pixel y
 *
 * The overlay div has pointer-events:auto only when a drawing tool is active.
 * cursor / crosshair modes pass all events through to LWC so normal pan/zoom works.
 */

import type { IChartApi, ISeriesApi, SeriesType, UTCTimestamp } from 'lightweight-charts'
import { useEffect, useRef, useState } from 'react'

import type { Drawing, DrawingPoint, DrawingType } from '@/stores/drawingStore'
import type { DrawingTool } from '@/stores/layoutStore'
import { cn } from '@/utils/cn'

// ── Constants ─────────────────────────────────────────────────────────────────

const DEFAULT_COLOR = '#2962ff'
const DEFAULT_WIDTH = 2
const HIT_THRESHOLD = 10 // px radius for eraser hit detection

// ── Types ──────────────────────────────────────────────────────────────────────

type Phase = 'idle' | 'awaiting-second' | 'dragging'

interface XY {
  x: number
  y: number
}

interface TextInput {
  point: DrawingPoint
  relX: number
  relY: number
  value: string
}

interface DrawingCanvasProps {
  chartRef: React.RefObject<IChartApi | null>
  seriesRef: React.RefObject<ISeriesApi<SeriesType> | null>
  activeTool: DrawingTool
  panelId: string
  drawings: Drawing[]
  onAdd: (drawing: Drawing) => void
  onRemove: (id: string) => void
  onUpdate: (id: string, patch: Partial<Pick<Drawing, 'color' | 'lineWidth' | 'text'>>) => void
}

// ── Component ──────────────────────────────────────────────────────────────────

const COLOR_SWATCHES = ['#2962ff', '#26a69a', '#ef5350', '#ffeb3b', '#ff9800', '#ffffff']

export function DrawingCanvas({
  chartRef,
  seriesRef,
  activeTool,
  drawings,
  onAdd,
  onRemove,
  onUpdate,
}: DrawingCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)

  // Increment to force a re-render when the chart scrolls / zooms
  const [, setTick] = useState(0)

  // Interaction state
  const [phase, setPhase] = useState<Phase>('idle')
  const anchorRef = useRef<DrawingPoint | null>(null)
  const [previewMouse, setPreviewMouse] = useState<DrawingPoint | null>(null)

  // Text tool
  const [textInput, setTextInput] = useState<TextInput | null>(null)

  // Freehand (pencil) tool — live points collected during drag, stored as state for preview
  const [livePoints, setLivePoints] = useState<DrawingPoint[]>([])
  const lastLiveXYRef = useRef<XY | null>(null) // last captured pixel position (for min-distance sampling)
  const isFreehandRef = useRef(false)

  // Selection (cursor mode) — id of the currently selected drawing
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // cursor passes through to LWC (pan/zoom); every other tool captures events
  const isCapturing = activeTool !== 'cursor'

  // Deselect when tool changes away from cursor
  useEffect(() => {
    if (activeTool !== 'cursor') setSelectedId(null)
  }, [activeTool])

  // Delete selected drawing with Delete/Backspace
  useEffect(() => {
    if (!selectedId) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        onRemove(selectedId)
        setSelectedId(null)
      }
      if (e.key === 'Escape') setSelectedId(null)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [selectedId, onRemove])

  // Window-level click listener for drawing selection in cursor mode.
  // SVG pointer-events on children inside a pointer-events:none parent div are
  // unreliable across browsers, and thin lines (2px) have a near-impossible
  // click target. This listener does the same pixel-distance hit-test as the
  // eraser, with a generous threshold, so any drawing type is easy to select.
  useEffect(() => {
    if (activeTool !== 'cursor') return
    const handler = (e: MouseEvent) => {
      const el = containerRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      // Ignore clicks outside the chart area
      if (
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom
      ) {
        setSelectedId(null)
        return
      }
      const px = e.clientX - rect.left
      const py = e.clientY - rect.top
      const W = el.clientWidth
      const chart = chartRef.current
      const series = seriesRef.current
      if (!chart || !series) return

      const toXYLocal = (pt: DrawingPoint): XY | null => {
        const x = chart.timeScale().timeToCoordinate(pt.time as UTCTimestamp)
        const y = series.priceToCoordinate(pt.price)
        if (x === null || y === null) return null
        return { x, y }
      }

      let best: { id: string; dist: number } | null = null

      for (const d of drawings) {
        const p1xy = d.points[0] ? toXYLocal(d.points[0]) : null
        const p2xy = d.points[1] ? toXYLocal(d.points[1]) : null
        let dist = Infinity

        if (d.type === 'hline' && p1xy) {
          dist = Math.abs(py - p1xy.y)
        } else if (d.type === 'vline' && p1xy) {
          dist = Math.abs(px - p1xy.x)
        } else if (d.type === 'trendline' && p1xy && p2xy) {
          const slope =
            Math.abs(p2xy.x - p1xy.x) < 0.5 ? Infinity : (p2xy.y - p1xy.y) / (p2xy.x - p1xy.x)
          const x1 = 0
          const y1 = Number.isFinite(slope) ? p1xy.y - slope * p1xy.x : p1xy.y
          const x2 = W
          const y2 = Number.isFinite(slope) ? p1xy.y + slope * (W - p1xy.x) : p1xy.y
          dist = distToSegment(px, py, x1, y1, x2, y2)
        } else if ((d.type === 'rectangle' || d.type === 'measure') && p1xy && p2xy) {
          const minX = Math.min(p1xy.x, p2xy.x)
          const maxX = Math.max(p1xy.x, p2xy.x)
          const minY = Math.min(p1xy.y, p2xy.y)
          const maxY = Math.max(p1xy.y, p2xy.y)
          dist =
            px >= minX && px <= maxX && py >= minY && py <= maxY
              ? 0
              : Math.min(
                  distToSegment(px, py, minX, minY, maxX, minY),
                  distToSegment(px, py, maxX, minY, maxX, maxY),
                  distToSegment(px, py, minX, maxY, maxX, maxY),
                  distToSegment(px, py, minX, minY, minX, maxY)
                )
        } else if (d.type === 'text' && p1xy) {
          dist = Math.hypot(px - p1xy.x, py - p1xy.y)
        } else if (d.type === 'freehand') {
          const pts = d.points.map((pt) => toXYLocal(pt)).filter((pt): pt is XY => pt !== null)
          for (let i = 0; i < pts.length - 1; i++) {
            const s = distToSegment(px, py, pts[i].x, pts[i].y, pts[i + 1].x, pts[i + 1].y)
            if (s < dist) dist = s
          }
        }

        if (dist < HIT_THRESHOLD * 2 && (!best || dist < best.dist)) {
          best = { id: d.id, dist }
        }
      }

      setSelectedId(best ? best.id : null)
    }
    window.addEventListener('click', handler)
    return () => window.removeEventListener('click', handler)
    // drawings reference changes only when drawings are added/removed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTool, drawings])

  // ── Subscribe to range changes so drawings stay aligned ──────────────────────

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    const handler = () => setTick((t) => t + 1)
    chart.timeScale().subscribeVisibleLogicalRangeChange(handler)
    return () => {
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(handler)
    }
  }, [chartRef])

  // ── Reset interaction state when tool changes ────────────────────────────────

  useEffect(() => {
    setPhase('idle')
    anchorRef.current = null
    setPreviewMouse(null)
    setTextInput(null)
    setLivePoints([])
    isFreehandRef.current = false
    lastLiveXYRef.current = null
  }, [activeTool])

  // ── Coordinate helpers ───────────────────────────────────────────────────────

  function clientToPoint(clientX: number, clientY: number): DrawingPoint | null {
    const chart = chartRef.current
    const series = seriesRef.current
    const el = containerRef.current
    if (!chart || !series || !el) return null
    const rect = el.getBoundingClientRect()
    const x = clientX - rect.left
    const y = clientY - rect.top
    const time = chart.timeScale().coordinateToTime(x)
    const price = series.coordinateToPrice(y)
    if (time === null || price === null) return null
    return { time: time as number, price }
  }

  function toXY(point: DrawingPoint): XY | null {
    const chart = chartRef.current
    const series = seriesRef.current
    if (!chart || !series) return null
    const x = chart.timeScale().timeToCoordinate(point.time as UTCTimestamp)
    const y = series.priceToCoordinate(point.price)
    if (x === null || y === null) return null
    return { x, y }
  }

  function containerSize(): XY {
    const el = containerRef.current
    return { x: el?.clientWidth ?? 0, y: el?.clientHeight ?? 0 }
  }

  // ── Geometry utilities ───────────────────────────────────────────────────────

  /** Extend a line through p1→p2 to the full SVG width. */
  function extendLine(p1: XY, p2: XY, W: number, H: number) {
    if (Math.abs(p2.x - p1.x) < 0.5) {
      return { x1: p1.x, y1: 0, x2: p1.x, y2: H }
    }
    const slope = (p2.y - p1.y) / (p2.x - p1.x)
    return {
      x1: 0,
      y1: p1.y - slope * p1.x,
      x2: W,
      y2: p1.y + slope * (W - p1.x),
    }
  }

  /** Shortest distance from point (px,py) to segment (x1,y1)→(x2,y2). */
  function distToSegment(px: number, py: number, x1: number, y1: number, x2: number, y2: number) {
    const dx = x2 - x1
    const dy = y2 - y1
    const lenSq = dx * dx + dy * dy
    if (lenSq === 0) return Math.hypot(px - x1, py - y1)
    const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lenSq))
    return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy))
  }

  // ── Commit a finished drawing ────────────────────────────────────────────────

  function commit(type: DrawingType, points: DrawingPoint[], text?: string) {
    onAdd({
      id: crypto.randomUUID(),
      type,
      points,
      text,
      color: DEFAULT_COLOR,
      lineWidth: DEFAULT_WIDTH,
    })
  }

  // ── Eraser hit-testing ───────────────────────────────────────────────────────

  function hitTestAndErase(clientX: number, clientY: number) {
    const el = containerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const px = clientX - rect.left
    const py = clientY - rect.top
    const { x: W, y: H } = containerSize()

    let best: { id: string; dist: number } | null = null

    for (const d of drawings) {
      const p1xy = d.points[0] ? toXY(d.points[0]) : null
      const p2xy = d.points[1] ? toXY(d.points[1]) : null
      let dist = Infinity

      if (d.type === 'hline' && p1xy) {
        dist = Math.abs(py - p1xy.y)
      } else if (d.type === 'vline' && p1xy) {
        dist = Math.abs(px - p1xy.x)
      } else if (d.type === 'trendline' && p1xy && p2xy) {
        const ext = extendLine(p1xy, p2xy, W, H)
        dist = distToSegment(px, py, ext.x1, ext.y1, ext.x2, ext.y2)
      } else if ((d.type === 'rectangle' || d.type === 'measure') && p1xy && p2xy) {
        const minX = Math.min(p1xy.x, p2xy.x)
        const maxX = Math.max(p1xy.x, p2xy.x)
        const minY = Math.min(p1xy.y, p2xy.y)
        const maxY = Math.max(p1xy.y, p2xy.y)
        if (
          px >= minX - HIT_THRESHOLD &&
          px <= maxX + HIT_THRESHOLD &&
          py >= minY - HIT_THRESHOLD &&
          py <= maxY + HIT_THRESHOLD
        ) {
          dist = 0
        }
      } else if (d.type === 'text' && p1xy) {
        dist = Math.hypot(px - p1xy.x, py - p1xy.y)
      } else if (d.type === 'freehand') {
        const pts = d.points.map((p) => toXY(p)).filter((p): p is XY => p !== null)
        for (let i = 0; i < pts.length - 1; i++) {
          const segDist = distToSegment(px, py, pts[i].x, pts[i].y, pts[i + 1].x, pts[i + 1].y)
          if (segDist < dist) dist = segDist
        }
      }

      if (dist < HIT_THRESHOLD && (!best || dist < best.dist)) {
        best = { id: d.id, dist }
      }
    }

    if (best) onRemove(best.id)
  }

  // ── Mouse event handlers ─────────────────────────────────────────────────────

  function handleMouseDown(e: React.MouseEvent) {
    if (!isCapturing || e.button !== 0) return
    const point = clientToPoint(e.clientX, e.clientY)
    if (!point) return

    switch (activeTool) {
      case 'crosshair': {
        // Pencil / freehand — start capturing a polyline
        isFreehandRef.current = true
        const startXY = toXY(point)
        lastLiveXYRef.current = startXY
        setLivePoints([point])
        break
      }

      case 'hline':
        commit('hline', [point])
        break

      case 'vline':
        commit('vline', [point])
        break

      case 'trendline':
        if (phase === 'idle') {
          anchorRef.current = point
          setPhase('awaiting-second')
        } else {
          const anchor = anchorRef.current
          if (anchor) commit('trendline', [anchor, point])
          anchorRef.current = null
          setPhase('idle')
          setPreviewMouse(null)
        }
        break

      case 'rectangle':
      case 'measure': {
        anchorRef.current = point
        setPhase('dragging')
        e.preventDefault()
        // Track start pixel position for drag-distance check (avoids toXY roundtrip which
        // can return null when the chart panned or coordinates are near the visible edge).
        const tool = activeTool
        const startX = e.clientX
        const startY = e.clientY
        const onWindowMove = (me: MouseEvent) => {
          const p = clientToPoint(me.clientX, me.clientY)
          setPreviewMouse(p)
        }
        const onWindowUp = (me: MouseEvent) => {
          window.removeEventListener('mousemove', onWindowMove)
          window.removeEventListener('mouseup', onWindowUp)
          const anchor = anchorRef.current
          const p = clientToPoint(me.clientX, me.clientY)
          const dx = me.clientX - startX
          const dy = me.clientY - startY
          if (anchor && p && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
            commit(tool, [anchor, p])
          }
          anchorRef.current = null
          setPhase('idle')
          setPreviewMouse(null)
        }
        window.addEventListener('mousemove', onWindowMove)
        window.addEventListener('mouseup', onWindowUp)
        break
      }

      case 'text': {
        const el = containerRef.current
        if (!el) break
        const rect = el.getBoundingClientRect()
        setTextInput({
          point,
          relX: e.clientX - rect.left,
          relY: e.clientY - rect.top,
          value: '',
        })
        break
      }

      case 'eraser':
        hitTestAndErase(e.clientX, e.clientY)
        break
    }
  }

  function handleMouseMove(e: React.MouseEvent) {
    if (!isCapturing) return
    const point = clientToPoint(e.clientX, e.clientY)
    setPreviewMouse(point)

    // Freehand: append point when mouse has moved at least 4px from last captured point
    if (activeTool === 'crosshair' && isFreehandRef.current && point) {
      const curXY = toXY(point)
      const last = lastLiveXYRef.current
      if (curXY && (!last || Math.hypot(curXY.x - last.x, curXY.y - last.y) >= 4)) {
        lastLiveXYRef.current = curXY
        setLivePoints((prev) => [...prev, point])
      }
    }
  }

  function handleMouseUp(_e: React.MouseEvent) {
    // Freehand commit
    if (activeTool === 'crosshair' && isFreehandRef.current) {
      isFreehandRef.current = false
      lastLiveXYRef.current = null
      if (livePoints.length >= 2) {
        commit('freehand', livePoints)
      }
      setLivePoints([])
      return
    }

    // rectangle / measure use window-level listeners (attached in handleMouseDown)
    // so this React handler intentionally does nothing for those tools.
    if (!isCapturing || phase !== 'dragging') return
    if (activeTool === 'rectangle' || activeTool === 'measure') return
  }

  // ── Text submit ──────────────────────────────────────────────────────────────

  function submitText() {
    if (!textInput) return
    if (textInput.value.trim()) {
      commit('text', [textInput.point], textInput.value.trim())
    }
    setTextInput(null)
  }

  // ── SVG rendering ────────────────────────────────────────────────────────────

  const { x: W, y: H } = containerSize()

  function renderDrawing(d: Drawing) {
    const p1xy = d.points[0] ? toXY(d.points[0]) : null
    const p2xy = d.points[1] ? toXY(d.points[1]) : null
    if (!p1xy) return null

    const stroke = d.color
    const sw = d.lineWidth
    const isSel = selectedId === d.id

    switch (d.type) {
      case 'hline':
        return (
          <g key={d.id}>
            {isSel && (
              <line
                x1={0}
                y1={p1xy.y}
                x2={W}
                y2={p1xy.y}
                stroke="white"
                strokeWidth={sw + 6}
                opacity={0.18}
              />
            )}
            <line x1={0} y1={p1xy.y} x2={W} y2={p1xy.y} stroke={stroke} strokeWidth={sw} />
            <rect x={W - 56} y={p1xy.y - 9} width={54} height={16} rx={2} fill={stroke} />
            <text
              x={W - 3}
              y={p1xy.y + 4}
              textAnchor="end"
              fill="white"
              fontSize={10}
              fontFamily="monospace"
            >
              {d.points[0].price.toFixed(2)}
            </text>
          </g>
        )

      case 'vline':
        return (
          <g key={d.id}>
            {isSel && (
              <line
                x1={p1xy.x}
                y1={0}
                x2={p1xy.x}
                y2={H}
                stroke="white"
                strokeWidth={sw + 4}
                opacity={0.18}
              />
            )}
            <line x1={p1xy.x} y1={0} x2={p1xy.x} y2={H} stroke={stroke} strokeWidth={sw} />
          </g>
        )

      case 'trendline': {
        if (!p2xy) return null
        const ext = extendLine(p1xy, p2xy, W, H)
        return (
          <g key={d.id}>
            {isSel && (
              <line
                x1={ext.x1}
                y1={ext.y1}
                x2={ext.x2}
                y2={ext.y2}
                stroke="white"
                strokeWidth={sw + 6}
                opacity={0.18}
              />
            )}
            <line
              x1={ext.x1}
              y1={ext.y1}
              x2={ext.x2}
              y2={ext.y2}
              stroke={stroke}
              strokeWidth={sw}
            />
          </g>
        )
      }

      case 'rectangle': {
        if (!p2xy) return null
        const rx = Math.min(p1xy.x, p2xy.x)
        const ry = Math.min(p1xy.y, p2xy.y)
        const rw = Math.abs(p2xy.x - p1xy.x)
        const rh = Math.abs(p2xy.y - p1xy.y)
        return (
          <g key={d.id}>
            {isSel && (
              <rect
                x={rx - 2}
                y={ry - 2}
                width={rw + 4}
                height={rh + 4}
                fill="none"
                stroke="white"
                strokeWidth={2}
                opacity={0.25}
                strokeDasharray="4 2"
              />
            )}
            <rect
              x={rx}
              y={ry}
              width={rw}
              height={rh}
              fill={`${stroke}18`}
              stroke={stroke}
              strokeWidth={sw}
            />
          </g>
        )
      }

      case 'measure': {
        if (!p2xy) return null
        const rx = Math.min(p1xy.x, p2xy.x)
        const ry = Math.min(p1xy.y, p2xy.y)
        const rw = Math.abs(p2xy.x - p1xy.x)
        const rh = Math.abs(p2xy.y - p1xy.y)
        const priceDiff = d.points[1].price - d.points[0].price
        const pct = ((priceDiff / d.points[0].price) * 100).toFixed(2)
        const sign = priceDiff >= 0 ? '+' : ''
        const label = `${sign}${pct}%`
        const fill = priceDiff >= 0 ? 'rgba(38,166,154,0.15)' : 'rgba(239,83,80,0.15)'
        const ac = priceDiff >= 0 ? '#26a69a' : '#ef5350'
        return (
          <g key={d.id}>
            {isSel && (
              <rect
                x={rx - 2}
                y={ry - 2}
                width={rw + 4}
                height={rh + 4}
                fill="none"
                stroke="white"
                strokeWidth={2}
                opacity={0.25}
                strokeDasharray="4 2"
              />
            )}
            <rect x={rx} y={ry} width={rw} height={rh} fill={fill} stroke={ac} strokeWidth={1} />
            {rw > 42 && rh > 18 && (
              <>
                <rect
                  x={rx + rw / 2 - 22}
                  y={ry + rh / 2 - 9}
                  width={44}
                  height={17}
                  rx={3}
                  fill={ac}
                />
                <text
                  x={rx + rw / 2}
                  y={ry + rh / 2 + 4}
                  textAnchor="middle"
                  fill="white"
                  fontSize={10}
                  fontFamily="monospace"
                >
                  {label}
                </text>
              </>
            )}
          </g>
        )
      }

      case 'text': {
        const label = d.text ?? ''
        const charW = 7
        const labelW = label.length * charW + 6
        return (
          <g key={d.id}>
            {isSel && (
              <rect
                x={p1xy.x - 4}
                y={p1xy.y - 16}
                width={labelW + 4}
                height={21}
                rx={2}
                fill="none"
                stroke="white"
                strokeWidth={1.5}
                opacity={0.35}
                strokeDasharray="3 2"
              />
            )}
            <rect
              x={p1xy.x - 2}
              y={p1xy.y - 14}
              width={labelW}
              height={17}
              rx={2}
              fill="rgba(19,23,34,0.82)"
            />
            <text
              x={p1xy.x}
              y={p1xy.y}
              fill={stroke}
              fontSize={12}
              fontFamily="ui-monospace, monospace"
            >
              {label}
            </text>
          </g>
        )
      }

      case 'freehand': {
        if (d.points.length < 2) return null
        const pts = d.points.map((p) => toXY(p)).filter((p): p is XY => p !== null)
        if (pts.length < 2) return null
        const pointsStr = pts.map((p) => `${p.x},${p.y}`).join(' ')
        return (
          <g key={d.id}>
            <polyline
              points={pointsStr}
              fill="none"
              stroke={stroke}
              strokeWidth={sw}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        )
      }

      default:
        return null
    }
  }

  function renderPreview() {
    if (!previewMouse) return null
    const mouseXY = toXY(previewMouse)
    if (!mouseXY) return null

    // Trendline: show dashed line from anchor to current mouse
    if (activeTool === 'trendline' && phase === 'awaiting-second') {
      const anchor = anchorRef.current
      if (!anchor) return null
      const anchorXY = toXY(anchor)
      if (!anchorXY) return null
      const ext = extendLine(anchorXY, mouseXY, W, H)
      return (
        <>
          <circle cx={anchorXY.x} cy={anchorXY.y} r={4} fill={DEFAULT_COLOR} opacity={0.8} />
          <line
            x1={ext.x1}
            y1={ext.y1}
            x2={ext.x2}
            y2={ext.y2}
            stroke={DEFAULT_COLOR}
            strokeWidth={DEFAULT_WIDTH}
            strokeDasharray="5 4"
            opacity={0.7}
          />
        </>
      )
    }

    // Rectangle / measure drag preview
    if ((activeTool === 'rectangle' || activeTool === 'measure') && phase === 'dragging') {
      const anchor = anchorRef.current
      if (!anchor) return null
      const anchorXY = toXY(anchor)
      if (!anchorXY) return null
      const rx = Math.min(anchorXY.x, mouseXY.x)
      const ry = Math.min(anchorXY.y, mouseXY.y)
      const rw = Math.abs(mouseXY.x - anchorXY.x)
      const rh = Math.abs(mouseXY.y - anchorXY.y)

      if (activeTool === 'measure') {
        const priceDiff = previewMouse.price - anchor.price
        const pct = ((priceDiff / anchor.price) * 100).toFixed(2)
        const sign = priceDiff >= 0 ? '+' : ''
        const label = `${sign}${pct}%`
        const fill = priceDiff >= 0 ? 'rgba(38,166,154,0.12)' : 'rgba(239,83,80,0.12)'
        const ac = priceDiff >= 0 ? '#26a69a' : '#ef5350'
        return (
          <>
            <rect
              x={rx}
              y={ry}
              width={rw}
              height={rh}
              fill={fill}
              stroke={ac}
              strokeWidth={1}
              strokeDasharray="4 3"
            />
            {rw > 42 && rh > 18 && (
              <>
                <rect
                  x={rx + rw / 2 - 22}
                  y={ry + rh / 2 - 9}
                  width={44}
                  height={17}
                  rx={3}
                  fill={ac}
                />
                <text
                  x={rx + rw / 2}
                  y={ry + rh / 2 + 4}
                  textAnchor="middle"
                  fill="white"
                  fontSize={10}
                  fontFamily="monospace"
                >
                  {label}
                </text>
              </>
            )}
          </>
        )
      }

      return (
        <rect
          x={rx}
          y={ry}
          width={rw}
          height={rh}
          fill={`${DEFAULT_COLOR}18`}
          stroke={DEFAULT_COLOR}
          strokeWidth={DEFAULT_WIDTH}
          strokeDasharray="5 4"
          opacity={0.7}
        />
      )
    }

    // Freehand live path
    if (activeTool === 'crosshair' && livePoints.length >= 2) {
      const pts = livePoints.map((p) => toXY(p)).filter((p): p is XY => p !== null)
      if (pts.length >= 2) {
        const pointsStr = pts.map((p) => `${p.x},${p.y}`).join(' ')
        return (
          <polyline
            points={pointsStr}
            fill="none"
            stroke={DEFAULT_COLOR}
            strokeWidth={DEFAULT_WIDTH}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )
      }
    }

    // Crosshair snap dot — shows where hline/vline will land
    if (activeTool === 'hline') {
      return (
        <line
          x1={0}
          y1={mouseXY.y}
          x2={W}
          y2={mouseXY.y}
          stroke={DEFAULT_COLOR}
          strokeWidth={1}
          strokeDasharray="3 3"
          opacity={0.5}
        />
      )
    }
    if (activeTool === 'vline') {
      return (
        <line
          x1={mouseXY.x}
          y1={0}
          x2={mouseXY.x}
          y2={H}
          stroke={DEFAULT_COLOR}
          strokeWidth={1}
          strokeDasharray="3 3"
          opacity={0.5}
        />
      )
    }

    return null
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  const cursorStyle = !isCapturing
    ? 'pointer-events-none'
    : activeTool === 'eraser'
      ? 'cursor-cell'
      : 'cursor-crosshair'

  return (
    <div
      ref={containerRef}
      className={cn('absolute inset-0 z-10', cursorStyle)}
      onMouseDown={isCapturing ? handleMouseDown : undefined}
      onMouseMove={isCapturing ? handleMouseMove : undefined}
      onMouseUp={isCapturing ? handleMouseUp : undefined}
    >
      <svg
        ref={svgRef}
        className="h-full w-full"
        style={{ pointerEvents: 'none', overflow: 'visible' }}
      >
        {drawings.map((d) => renderDrawing(d))}
        {renderPreview()}
      </svg>

      {/* Floating text input for text tool */}
      {textInput && (
        <div
          className="absolute z-20"
          style={{ left: textInput.relX, top: textInput.relY - 22 }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <input
            autoFocus
            type="text"
            value={textInput.value}
            onChange={(e) => setTextInput({ ...textInput, value: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitText()
              if (e.key === 'Escape') setTextInput(null)
            }}
            className="w-44 rounded border border-tv-blue bg-tv-bg-primary px-2 py-0.5 text-xs text-tv-text-primary shadow-lg outline-none ring-1 ring-tv-blue/40"
            placeholder="Type label, Enter to place…"
          />
        </div>
      )}

      {/* Selection action bar — appears when a drawing is selected in cursor mode */}
      {selectedId &&
        activeTool === 'cursor' &&
        (() => {
          const d = drawings.find((x) => x.id === selectedId)
          if (!d) return null
          return (
            <div
              className="absolute left-1/2 top-2 z-30 flex -translate-x-1/2 items-center gap-1 rounded border border-tv-border bg-tv-bg-secondary px-2 py-1 shadow-lg"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <span className="mr-1 text-[10px] text-tv-text-muted">Color:</span>
              {COLOR_SWATCHES.map((c) => (
                <button
                  key={c}
                  type="button"
                  title={c}
                  onClick={() => onUpdate(selectedId, { color: c })}
                  className="h-4 w-4 rounded-full border-2 transition-transform hover:scale-110"
                  style={{
                    backgroundColor: c,
                    borderColor: d.color === c ? 'white' : 'transparent',
                  }}
                />
              ))}
              <div className="mx-1 h-4 w-px bg-tv-border" />
              <button
                type="button"
                title="Delete (Del)"
                onClick={() => {
                  onRemove(selectedId)
                  setSelectedId(null)
                }}
                className="rounded px-1.5 py-0.5 text-[10px] text-tv-red hover:bg-tv-red/10"
              >
                Delete
              </button>
              <button
                type="button"
                title="Close"
                onClick={() => setSelectedId(null)}
                className="ml-0.5 rounded px-1 py-0.5 text-[10px] text-tv-text-muted hover:text-tv-text-primary"
              >
                ✕
              </button>
            </div>
          )
        })()}
    </div>
  )
}
